/**
 * Pure helpers for the report export paths. Extracted from ReportViewer so the
 * rules can be asserted directly -- the PDF cap already shipped one live defect
 * (the preview exemption, JFCRX-6732 AC12) that a unit test would have caught.
 */

/**
 * @react-pdf lays out and renders the whole document in a single pass, on the
 * main thread, and the cost grows with CELLS rather than rows -- at a fixed
 * 2,000 rows, 5 columns takes 2.39s and 30 columns 24.14s. Measured against
 * synthetic rows at 11 columns: 50k cells ~16s, 110k ~49s, 220k ~171s, and the
 * 77,136-row production report never completes.
 */
export const MAX_CELL_COUNT_FOR_PDF_EXPORT = 50000;

/**
 * What one Substitution row costs the renderer.
 *
 * The Substitution PDF does NOT draw one cell per column. PDFReportDocument
 * slices every row to its first ten values for the table row -- MAIN_ROW_CELLS
 * in Substitute/pdfReportGeneration/PDFReportDocument.js (:38) -- then draws
 * columns 10-14 underneath it as five LABEL AND VALUE pairs -- Rx #, patient
 * name, order id, device and date, the detail line at :165-278. Ten plus ten:
 * 20 <Text> nodes carrying 15 columns, every one @react-pdf lays out.
 *
 * (Those two references were :29-32 and :130-243 until JFCRX-6781 rewrote that
 * function; they are anchored to the named constant now so the next rewrite
 * moves them less.)
 *
 * Spending the budget at the report's 15 columns would allow 3,333 rows and
 * draw ~66,600 nodes -- a third more than MAX_CELL_COUNT_FOR_PDF_EXPORT was
 * measured to buy. 20 is what is drawn, so 20 is what gets spent: 2,500 rows.
 *
 * This is a property of that document layout, not of the report definition, so
 * it does not follow reportObj.fields. If the detail line gains or loses a
 * pair, change it here.
 */
export const PDF_TEXT_NODES_PER_SUBSTITUTION_ROW = 20;

/**
 * Whether a PDF export of `rowCount` rows over `columnCount` columns is beyond
 * what the browser can render.
 *
 * `isPreview` is exempt: run2 clamps `limit` to 10 server-side when previewing
 * (ReportGenerator2.ts:327) even though the export asks for pageSize 0, while
 * the row count the caller has is $count over the whole set. Without the
 * exemption a preview is refused for rows it will never render.
 */
export const isPdfExportTooLarge = ({ rowCount, columnCount, isPreview }) => {
  if (isPreview) return false;
  if (!Number.isFinite(rowCount) || rowCount <= 0) return false;
  if (!Number.isFinite(columnCount) || columnCount <= 0) return false;
  return rowCount * columnCount > MAX_CELL_COUNT_FOR_PDF_EXPORT;
};

/** The row limit for a report of this width. 0 when the width is unusable. */
export const maxPdfExportRows = (columnCount) =>
  !Number.isFinite(columnCount) || columnCount <= 0
    ? 0
    : Math.floor(MAX_CELL_COUNT_FOR_PDF_EXPORT / columnCount);

/**
 * The rows the UNPAGINATED PDF path serialises: `largeViewData`, or the groups
 * flattened back into rows when a field is grouped.
 *
 * JFCRX-6782. This is now the array the Substitution PDF viewer is HANDED, not
 * merely the array the cap counts. PDFReportDocument has no grouping handling
 * at all: given a group it treats that group as a row, so the ten cells it
 * draws become the group's first ten ROWS and the detail line beneath them
 * takes the eleventh to fifteenth. The flattening therefore has to happen
 * before the handoff. The shared builder viewer already did exactly this
 * (ReportViewer.js:1302-1310); the Substitution copy never did.
 *
 * ONE definition, used by both the guard and the handoff, is the point. The
 * rows JFCRX-6749 charges for are then the rows the document draws -- by
 * construction, not by argument. Flattening here and counting somewhere else
 * is how the two silently stop describing each other.
 *
 * Grouping partitions the rows -- every row lands in exactly one group -- so
 * the flattened set never exceeds `largeViewData`; non-array groups are
 * dropped, so it can be smaller.
 *
 * The ungrouped branch returns `largeViewData` itself rather than a copy. No
 * caller mutates it: the document maps over it and the preview slices it.
 */
export const getOnScreenExportRows = ({
  groupedField,
  largeViewData,
  groupedData,
}) => {
  if (!groupedField || groupedField === "No grouping") {
    return Array.isArray(largeViewData) ? largeViewData : [];
  }
  if (!Array.isArray(groupedData)) return [];
  return groupedData.flatMap((group) => (Array.isArray(group) ? group : []));
};

/**
 * How many rows that is. Derived from the rows themselves so the cap cannot
 * count one set while the document draws another.
 */
export const getOnScreenExportRowCount = (args) =>
  getOnScreenExportRows(args).length;

/**
 * The Substitution PDF cap, decided in one place.
 *
 * Composed here rather than at the call site because that is what makes the
 * decision testable. The three helpers above are each covered on their own,
 * but nothing would have caught the CALL SITE spending the budget on the
 * report's 15 columns instead of the 20 nodes the document draws: every test
 * would still pass and the cap would sit a third above what the budget buys.
 * Now the multiplier is exercised by the same path the viewer uses, and
 * `maxRows` comes back from here too, so the number offered to the user cannot
 * drift from the number enforced.
 *
 * `isPreview` cannot actually fire the exemption on THIS report: run2 clamps a
 * preview to 10 rows server-side, so the rows on screen are already at most 10
 * against a cap of 2,500. It is threaded through because JFCRX-6749 AC6
 * requires the exemption, and because the shared viewer needs it for real --
 * its count is a server $count over the whole set, taken before the clamp.
 *
 * GROUPED, THE COUNT IS NOW EXACT -- it was deliberately conservative until
 * JFCRX-6782. It flattens the groups, and until that fix PDFReportDocument had
 * no grouping handling at all and drew one row per GROUP, so the guard charged
 * for more nodes than were drawn and erred toward refusing. The PDF path is
 * handed getOnScreenExportRows() now, which is the same flattening this counts,
 * so charged rows and drawn rows are the same set.
 */
export const isSubstitutionPdfExportTooLarge = ({
  groupedField,
  largeViewData,
  groupedData,
  isPreview,
}) => {
  const rowCount = getOnScreenExportRowCount({
    groupedField,
    largeViewData,
    groupedData,
  });
  return {
    rowCount,
    tooLarge: isPdfExportTooLarge({
      rowCount,
      columnCount: PDF_TEXT_NODES_PER_SUBSTITUTION_ROW,
      isPreview,
    }),
    maxRows: maxPdfExportRows(PDF_TEXT_NODES_PER_SUBSTITUTION_ROW),
  };
};

/**
 * The PDF cap for a viewer that PAGINATES, decided in one place.
 *
 * JFCRX-6738. Composed here for the reason JFCRX-6749 composed
 * `isSubstitutionPdfExportTooLarge`: each helper below is covered on its own,
 * but nothing would catch the CALL SITE picking the wrong row count. That is
 * the part with a real bug surface here, because it differs by path:
 *
 *   - UNPAGINATED (`reportPageSize === 0`) the viewer serialises the rows on
 *     screen, so the flattened on-screen set is what gets drawn.
 *   - PAGINATED the click re-fetches the whole result set, and the only figure
 *     available before that fetch is `reportTotalRowCount` -- which describes
 *     the LAST run. It is an estimate, and the caller MUST re-check against the
 *     rows actually returned. This function cannot do that second check for it.
 *
 * `reportTotalRowCount` can be `undefined` -- that is JFCRX-6739, latent on
 * every viewer that does `setReportTotalRowCount(results[1][0]?.count)`. It is
 * coerced to 0 here rather than passed through, so the estimate is a number in
 * every case. 0 cannot exceed the budget, so an unknown count falls through to
 * the authoritative post-fetch check rather than being refused on a guess.
 *
 * Unlike the Substitution document, the viewers that use this draw exactly one
 * <Text> per cell (Reports2/PDFReportViewer.js), so `columnCount` is the
 * report's column count and not a per-row node count.
 */
export const isPaginatedPdfExportTooLarge = ({
  reportPageSize,
  reportTotalRowCount,
  groupedField,
  largeViewData,
  groupedData,
  columnCount,
  isPreview,
}) => {
  const rowCount =
    reportPageSize === 0
      ? getOnScreenExportRowCount({ groupedField, largeViewData, groupedData })
      : Number.isFinite(reportTotalRowCount)
        ? reportTotalRowCount
        : 0;
  return {
    rowCount,
    tooLarge: isPdfExportTooLarge({ rowCount, columnCount, isPreview }),
    maxRows: maxPdfExportRows(columnCount),
  };
};

/**
 * Label every exported column, preserving the fallback the server used to
 * apply when it keyed records by header string
 * (`this.reportHeaders[index] ?? `Column${index + 1}``).
 *
 * Rows are positional now, so a missing or blank header would otherwise reach
 * the CSV as an unlabelled column rather than a named one. `columnCount` pads
 * the list if the rows are ever wider than the headers.
 */
export const labelExportHeaders = (headers, columnCount) => {
  const source = Array.isArray(headers) ? headers : [];
  const width = Number.isFinite(columnCount)
    ? Math.max(columnCount, source.length)
    : source.length;
  return Array.from(
    { length: width },
    (_, i) => source[i] || `Column${i + 1}`,
  );
};

/**
 * JFCRX-7650. Rows from downloadDataForCSV arrive with dates as ISO strings
 * (the same contract as /run2). Format them the way the grid does: the same
 * formatDate closure, chosen by the same dataType === "Date" rule, so the
 * paginated Excel and PDF agree with the screen in zone and in day order.
 * `fields` is the viewer's combinedReportFields; column i is fields[i].
 * Everything that is not a Date column passes through untouched. Returns new
 * arrays; the input is not mutated.
 *
 * A blank Date cell (null, undefined, "", 0) becomes "", which is what the
 * viewers' formatDate returns for it, so the file and the grid agree and all
 * blank dates fall into one group, as they do on screen.
 *
 * Run it BEFORE orderExportRowsByGroup: the grid groups by the displayed,
 * minute-precision string, so two records seconds apart share one on-screen
 * group but would split into two if grouped by their full ISO values.
 */
export const formatExportRowDates = (rows, { fields, formatDate }) =>
  (rows ?? []).map((row) =>
    (row ?? []).map((cell, i) =>
      fields?.[i]?.dataType === "Date" ? (cell ? formatDate(cell) : "") : cell,
    ),
  );

/**
 * JFCRX-7643. The PDF preview renders ten rows per document, one document per
 * pager page, so react-pdf's own page counter inside the preview always reads
 * "1 / 1". The preview footer therefore states the PREVIEW position instead,
 * and this is the one place the preview page size and page count are defined:
 * the three preview viewers and both pagers read them from here, so the footer
 * and the pager cannot disagree. The download is a different document and
 * keeps react-pdf's real page numbers.
 */
export const PDF_PREVIEW_ROWS = 10;

export const getPdfPreviewPageCount = (totalItems) =>
  Math.max(1, Math.ceil((totalItems ?? 0) / PDF_PREVIEW_ROWS));

/**
 * The footer text for one physical PDF page, used by all three documents.
 *
 * A download supplies no `footerLabel` and gets react-pdf's real counter. A
 * preview supplies "Preview page 3 of 62". If react-pdf still had to wrap
 * that ten-row slice across physical pages (long cells at Large Font), the
 * physical position is appended so the two pages do not read identically:
 * "Preview page 3 of 62 (2 / 2)". A blank label counts as no label, so an
 * empty locale string can never print an empty footer.
 */
export const formatPdfFooter = ({ footerLabel, pageNumber, totalPages }) => {
  const counter = `${pageNumber} / ${totalPages}`;
  if (!footerLabel) return counter;
  return totalPages > 1 ? `${footerLabel} (${counter})` : footerLabel;
};

/**
 * JFCRX-7649. The grid's grouping rule, in one place. Groups are opened in
 * order of first appearance of the cell at `columnIndex`, each holds its rows
 * in their original order, and cells compare by value (SameValueZero), so
 * blanks group with blanks but "" and undefined are different groups -- that
 * is what the viewer effects did with `searched.includes` and `===`.
 * Rows that are not arrays are skipped. A negative or non-integer columnIndex
 * gives [] -- "No grouping" resolves to -1 and the effects store [] for it.
 * O(n) where the effects were O(n^2).
 */
export const groupRowsByColumn = (rows, columnIndex) => {
  if (!Array.isArray(rows) || !Number.isInteger(columnIndex) || columnIndex < 0)
    return [];
  const groups = new Map();
  rows.forEach((row) => {
    if (!Array.isArray(row)) return;
    const key = row[columnIndex];
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  });
  return Array.from(groups.values());
};

/** Index of `groupedField` ("collection:name") in `fields`; -1 if absent or not grouped. */
export const getGroupedColumnIndex = (fields, groupedField) =>
  !groupedField || groupedField === "No grouping" || !Array.isArray(fields)
    ? -1
    : fields.findIndex((f) => `${f?.collection}:${f?.name}` === groupedField);

/**
 * JFCRX-7649. Server rows from downloadDataForCSV, put in the order the grid
 * shows them: grouped by the same column with the same rule, then flattened,
 * so the paginated file reads like the unpaginated one. Ungrouped, or when the
 * grouped field is not among `fields`, the rows come back as they are, never
 * empty. Every input row lands in the output exactly once.
 */
export const orderExportRowsByGroup = ({ rows, groupedField, fields }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const columnIndex = getGroupedColumnIndex(fields, groupedField);
  if (columnIndex < 0) return safeRows;
  return groupRowsByColumn(safeRows, columnIndex).flat();
};

/** The formatting ReportExcelExporter has always applied to a total. */
const formatExportTotal = (n) =>
  n.toFixed(2).replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/, "$1");

/**
 * JFCRX-7649. Everything the spreadsheet export writes, as positional rows:
 * `headers` (labelled with the same fallback the paginated path has used since
 * JFCRX-6732, so a blank header becomes ColumnN in both files) and `rows`,
 * which are the data rows followed by a SUM row and an AVG row -- each only
 * when at least one column defines that total (a total is "defined" when the
 * viewer's columnSums / columnAverages entry is >= 0; -1 is the viewer's
 * "none" sentinel and NaN is "could not be computed", both omitted).
 *
 * Positional on purpose: keyed by header string, two columns with the same
 * display name collapsed into one (JFCRX-6732; "Pill Weight" appears three
 * times in the product namespace alone). Positional, both are written.
 */
export const buildExcelExportTable = ({
  headers,
  rows,
  columnSums,
  columnAverages,
}) => {
  const dataRows = Array.isArray(rows) ? rows.filter(Array.isArray) : [];
  // reduce, not Math.max(...spread): the paginated path fetches the whole
  // set, and spreading one argument per row overflows the call stack somewhere
  // past 100k rows.
  const width = dataRows.reduce(
    (w, r) => (r.length > w ? r.length : w),
    Array.isArray(headers) ? headers.length : 0,
  );
  const labelled = labelExportHeaders(headers, width);
  const totalRow = (totals) =>
    labelled.map((_, i) =>
      Number.isFinite(totals?.[i]) && totals[i] >= 0
        ? formatExportTotal(totals[i])
        : "",
    );
  // Only totals inside the header width count: one past it would otherwise
  // append a row of nothing but blanks.
  const hasAny = (totals) =>
    labelled.some((_, i) => Number.isFinite(totals?.[i]) && totals[i] >= 0);
  const out = [...dataRows];
  if (hasAny(columnSums)) out.push(totalRow(columnSums));
  if (hasAny(columnAverages)) out.push(totalRow(columnAverages));
  return { headers: labelled, rows: out };
};

/**
 * JFCRX-7649. The ids for one getByIds request, in batches. Both lookup
 * services send their ids as repeated GET query parameters, so a request over
 * the whole fetched set (616 rows on QA) builds a URL past the server's header
 * limit and is answered 503. Unique, non-blank ids only, at most `size` per
 * batch. (A one-id batch reaches the server as a string, not an array; both
 * controllers now accept either.)
 */
export const chunkLookupIds = (ids, size) => {
  const unique = [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean))];
  const chunks = [];
  for (let i = 0; i < unique.length; i += size) {
    chunks.push(unique.slice(i, i + size));
  }
  return chunks;
};

/**
 * JFCRX-7649. Tray Check Summary's Facility Name and Facility Id columns
 * (indices 2 and 3) are `customized` fields the server writes as "". This
 * fills them from the dispensing order id in column 4: order ids -> orders
 * (each carries its facility's _id) -> facilities (name and facilityId).
 * Mutates and returns `rows`, as the viewer's run handler always did; a row
 * with no order id, or whose order or facility is not found, is left as it
 * came. Both lookups go out through `chunkLookupIds` in batches of
 * `batchSize`, because the services put the ids in the GET query string and
 * one request over the whole fetched set was answered 503 (616 rows,
 * 2026-11-03). Batches run one after another, so a large set does not burst
 * the server; a batch that resolves to anything but an array contributes
 * nothing.
 *
 * `getDispensingOrders(ids)` and `getFacilities(ids)` are the two service
 * calls with the user already bound, so this stays a plain function.
 */
export const fillFacilityColumns = (
  rows,
  { getDispensingOrders, getFacilities, batchSize = 100 },
) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const inBatches = (fetch, ids) =>
    chunkLookupIds(ids, batchSize).reduce(
      (chain, batch) =>
        chain.then((acc) =>
          Promise.resolve(fetch(batch)).then((r) =>
            Array.isArray(r) ? acc.concat(r) : acc,
          ),
        ),
      Promise.resolve([]),
    );
  const orderIds = safeRows.map((row) =>
    Array.isArray(row) ? row[4] : undefined,
  );
  return inBatches(getDispensingOrders, orderIds).then((orders) => {
    /* JFCRX-6738. Direct assignment, not `x = {...x, [k]: v}` per iteration,
     * which copied every key already in the accumulator (O(n^2)). */
    const facilityIdByOrderId = {};
    const facilityIds = [];
    orders.forEach((order) => {
      facilityIds.push(order?.facility);
      if (order?.dispensingOrderId) {
        facilityIdByOrderId[order.dispensingOrderId] = order.facility;
      }
    });
    return inBatches(getFacilities, facilityIds).then((facilities) => {
      /* JFCRX-6738. Built once, outside the row loop. */
      const facilityById = {};
      facilities.forEach((facility) => {
        const name = facility?.name;
        const facilityId = facility?.facilityId;
        if (facility?._id && (name || facilityId)) {
          facilityById[facility._id] = { name, facilityId };
        }
      });
      safeRows.forEach((row) => {
        if (!Array.isArray(row) || !row[4]) return;
        const facility = facilityById[facilityIdByOrderId[row[4]]];
        if (facility?.name) row[2] = facility.name;
        if (facility?.facilityId) row[3] = facility.facilityId;
      });
      return safeRows;
    });
  });
};
